import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";

//PROVIDERS
import { Provider } from "react-redux";
import { NextUIProvider } from "@nextui-org/react";
import { QueryClientProvider } from "@tanstack/react-query";

//COMPONENTS
import App from "./App.tsx";
import { queryClient } from "./queryClient";

//UTILS
import { store } from "./store/store";
import { bridgeConsoleToLogger } from "./utils/logger";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

CapacitorUpdater.notifyAppReady();

//STYLES
import "./styles/globals.scss";

bridgeConsoleToLogger();

// Reload when a lazy chunk fails to load (usually a stale tab after a deploy).
// Guarded with a cooldown to avoid an infinite reload loop if the chunk is
// genuinely broken: if it fails again within the cooldown window, we let the
// error bubble up to the ErrorBoundary instead of reloading.
const CHUNK_RELOAD_KEY = "chunk-reload-attempt-at";
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;
window.addEventListener("vite:preloadError", (event) => {
  const lastAttempt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  console.log("[chunk-reload] preloadError on URL:", window.location.pathname);
  if (Date.now() - lastAttempt < CHUNK_RELOAD_COOLDOWN_MS) {
    console.log("[chunk-reload] cooldown active, NOT reloading");
    return;
  }
  console.log("[chunk-reload] reloading current URL");
  event.preventDefault();
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENVIRONMENT ?? "dev",
  release: import.meta.env.VITE_SENTRY_RELEASE,
  sendDefaultPii: true,
  normalizeDepth: 10,

  integrations: [
    // Captures route navigation as transactions
    Sentry.browserTracingIntegration(),
    // Records the user session at the time of the error
    Sentry.replayIntegration(),
  ],

  tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === "prod" ? 0.1 : 1,

  // Records only 10% of normal sessions, but 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Disabled in dev to keep the dashboard clean
  enabled: import.meta.env.VITE_ENVIRONMENT === "prod",

  // Ignores errors that are not real bugs and adds Capacitor platform context
  beforeSend(event, hint) {
    const ignoredErrors = [
      "Failed to fetch dynamically imported module",
      "error loading dynamically imported module",
      "Importing a module script failed",
      "Load failed",
      "Network request failed",
      "ChunkLoadError",
      "Loading chunk",
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "Script error",
      "Network Error",
      "Unable to preload CSS",
      "Unexpected token '<'",
    ];

    const originalException = hint?.originalException;
    if (originalException instanceof Error) {
      if (ignoredErrors.some((e) => originalException.message?.includes(e))) {
        return null;
      }
    }

    // Tag all events with Capacitor platform info and app version
    event.tags = {
      ...event.tags,
      platform: Capacitor.getPlatform(), // "ios" | "android" | "web"
      isNative: Capacitor.isNativePlatform(),
    };

    return event;
  },

  // Removes noisy console.warn breadcrumbs
  beforeBreadcrumb(breadcrumb) {
    return breadcrumb.category === "console" && breadcrumb.level === "warning"
      ? null
      : breadcrumb;
  },
});

createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <NextUIProvider>
        <App />
      </NextUIProvider>
    </Provider>
  </QueryClientProvider>,
);
